// Runtime catalog store for Nofina Movie.
//
// Source of truth at runtime is the Nofina Movie backend public catalog API
// (GET /api/catalog), which serves the validated Last Known Good snapshot.
// The bundled local snapshot is used as an instant bootstrap and as an offline
// fallback for the session, so the storefront never shows a blank screen.

import { useSyncExternalStore, useEffect } from "react";
import snapshot from "@/data/catalogSnapshot.json";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Deterministic slug -> local icon file (bootstrap only; API provides iconUrl).
const ICON_FILE = {
  "vidio-private": "vidio.png", "netflix-private-4k-hdr": "netflix.png",
  "disney-hotstar": "disney.png", "youtube-premium": "youtube.png",
  "iqiyi-private": "iqiyi.png", "capcut-private": "capcut.png",
  "canva-team": "canva.png", netshort: "netshort.svg", dramabox: "dramabox.svg",
  dramawave: "dramawave.svg", vigloo: "vigloo.svg", flextv: "flextv.svg",
  joyreels: "joyreels.jpeg", cubetv: "cubetv.webp", vibeshort: "vibeshort.jpeg",
  goodshort: "goodshort.svg", flickreels: "flickreels.svg", melolo: "melolo.svg",
  idrama: "idrama.svg", moboreels: "moboreels.svg", shortmax: "shortmax.svg",
  reelshort: "reelshort.svg", "reelshort-apk": "reelshortapk.png",
  stardusttv: "stardusttv.svg", shortwave: "shortwave.webp",
  "dramanova-18": "dramanova.webp", shotshort: "shotshort.jpg", kalostv: "kalostv.png",
  wetv: "wetv.png", sereal: "sereal.png", "anyreel-18": "anyreel.png",
  freereels: "freereels.png", pinedrama: "pinedrama.png", dramabite: "dramabite.png",
  reelife: "reelife.png", storyreel: "storyreel.png", dramapops: "dramapops.png",
  flareflow: "flareflow.png", bstation: "bstation.png", moviebox: "moviebox.png",
  playlet: "playlet.png", "raptdrama-18": "raptdrama.png", shorten: "shorten.png",
  bonustv: "bonustv.png", snackshort: "snackshort.png", sodareels: "sodareels.png",
  reelbuzz: "reelbuzz.png", cashdrama: "cashdrama.png", rapidtv: "rapidtv.png",
  luminareels: "luminareels.png", bilitv: "bilitv.png",
};

const FEATURED = new Set([
  "netflix-private-4k-hdr", "vidio-private", "disney-hotstar", "youtube-premium",
  "iqiyi-private", "canva-team", "capcut-private", "wetv", "bstation",
  "dramabox", "moviebox", "netshort",
]);

function initialsOf(name) {
  const words = name.replace(/[^A-Za-z0-9 +]/g, "").trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

// Bootstrap normalization (bundled snapshot has no stock field -> AVAILABLE).
function normalizeBootstrap() {
  return snapshot.products.map((p, index) => ({
    id: p.slug, slug: p.slug, name: p.name,
    iconUrl: ICON_FILE[p.slug] ? `/logos/${ICON_FILE[p.slug]}` : "",
    initials: initialsOf(p.name), isFeatured: FEATURED.has(p.slug), sortOrder: index,
    variants: p.variants.map((v) => ({
      code: v.code, durationLabel: v.duration, price: v.price,
      stock: 1, available: true,
    })),
  }));
}

// API normalization -> the shape the UI components already consume.
function normalizeApi(apiProducts) {
  return apiProducts.map((p, index) => ({
    id: p.slug, slug: p.slug, name: p.name,
    iconUrl: p.iconUrl || (ICON_FILE[p.slug] ? `/logos/${ICON_FILE[p.slug]}` : ""),
    initials: initialsOf(p.name),
    isFeatured: p.isFeatured ?? FEATURED.has(p.slug),
    sortOrder: p.sortOrder ?? index,
    variants: (p.variants || []).map((v) => ({
      code: v.code, durationLabel: v.durationLabel, price: v.price,
      stock: v.stock, available: v.availability === "AVAILABLE" && v.stock > 0,
    })),
  }));
}

// --- Minimal external store ---
let state = { products: normalizeBootstrap(), loaded: false };
const listeners = new Set();
function emit() {
  listeners.forEach((l) => l());
}
function subscribe(l) {
  listeners.add(l);
  return () => listeners.delete(l);
}
function getSnapshot() {
  return state;
}

let loadStarted = false;
export async function loadCatalog() {
  if (loadStarted) return;
  loadStarted = true;
  try {
    const res = await fetch(`${BACKEND}/api/catalog`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    if (Array.isArray(data.products) && data.products.length > 0) {
      state = { products: normalizeApi(data.products), loaded: true };
      emit();
      return;
    }
    throw new Error("empty");
  } catch {
    // Keep bundled bootstrap for this session; no supplier-specific error shown.
    state = { products: state.products, loaded: true };
    emit();
  }
}

// React hook: triggers a one-time load and re-renders on updates.
export function useCatalog() {
  const snap = useSyncExternalStore(subscribe, getSnapshot);
  useEffect(() => {
    loadCatalog();
  }, []);
  return { products: snap.products, loaded: snap.loaded };
}

// --- Read helpers (operate on current store state) ---
export function getProducts() {
  return state.products;
}

export function getProductBySlug(slug) {
  return state.products.find((p) => p.slug === slug) || null;
}

export function searchProducts(query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return state.products;
  return state.products.filter((p) => p.name.toLowerCase().includes(q));
}

export function getFeaturedProducts(limit = 12) {
  return state.products.filter((p) => p.isFeatured).slice(0, limit);
}

export function getDefaultVariant(product) {
  return product.variants.find((v) => v.available) || product.variants[0];
}

export function variantIsOrderable(variant) {
  return Boolean(variant && variant.available && variant.price > 0);
}

// Current valid variant codes (used to validate the WhatsApp buy command).
export function getValidVariantCodes() {
  return new Set(state.products.flatMap((p) => p.variants.map((v) => v.code)));
}
